import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { getUserInfo } from '_utils/localData'
import { GROUP_IS_ADMIN, GROUP_IS_HOST } from '_utils/constant'

const useUserRole = (groupDetail = null) => {
  const reduxGroup = useSelector((state) => state.group?.groupById)
  const currentGroup = groupDetail || reduxGroup

  const userInfo = useMemo(() => getUserInfo() || null, [])

  const isHostOrAdmin = useMemo(() => {
    if (!userInfo?.groups?.length) return false
    return userInfo.groups.some(
      (g) =>
        g?.isHost === GROUP_IS_HOST ||
        g?.isAdmin === GROUP_IS_ADMIN ||
        g?.isHost === 1 ||
        g?.isAdmin === 1
    )
  }, [userInfo])

  const isHost = useMemo(() => {
    if (!currentGroup) return false
    return (
      currentGroup?.isHost === GROUP_IS_HOST ||
      currentGroup?.isHost === 1 ||
      currentGroup?.isHost === true ||
      currentGroup?.role === 'HOST'
    )
  }, [currentGroup])

  const isAdmin = useMemo(() => {
    if (!currentGroup) return false
    return (
      currentGroup?.isAdmin === GROUP_IS_ADMIN ||
      currentGroup?.isAdmin === 1 ||
      currentGroup?.isAdmin === true ||
      currentGroup?.role === 'ADMIN'
    )
  }, [currentGroup])

  const getTargetUserId = (target) => {
    if (!target) return null
    if (typeof target === 'string' || typeof target === 'number') return String(target)
    return String(target?.uid || target?.id || target?.userId || '')
  }

  const isCurrentUser = (target) => {
    const currentUserId = userInfo?.id || userInfo?.uid
    const targetId = getTargetUserId(target)
    return Boolean(currentUserId && targetId && String(currentUserId) === targetId)
  }

  const isGroupOwner = (target) => {
    if (!currentGroup?.createdUserId || !target) return false
    const targetId = getTargetUserId(target)
    return Boolean(targetId && String(currentGroup.createdUserId) === targetId)
  }

  const canModifyMember = (target) => {
    if (!target) return false
    return !isCurrentUser(target) && !isGroupOwner(target)
  }

  const isOwner = useMemo(
    () => isGroupOwner(userInfo),
    [currentGroup?.createdUserId, userInfo]
  )

  const adminOrHost = isHost || isAdmin

  const isAllowManageEvent = (eventDetail) => {
    if (isAdmin) return true
    if (eventDetail?.createdUserId && currentGroup?.uid) {
      return eventDetail.createdUserId === currentGroup.uid
    }
    if (eventDetail?.createdUserId && userInfo?.id) {
      return eventDetail.createdUserId === userInfo.id
    }
    return false
  }

  const getMemberRole = (memberInfo) => {
    if (memberInfo?.isAdmin === GROUP_IS_ADMIN || memberInfo?.isAdmin === 1) return 'ADMIN'
    if (memberInfo?.isHost === GROUP_IS_HOST || memberInfo?.isHost === 1) return 'HOST'
    return 'MEMBER'
  }

  return {
    userInfo,
    isOwner,
    isHostOrAdmin,
    isHost,
    isAdmin,
    adminOrHost,
    isAllowManageEvent,
    getMemberRole,
    isCurrentUser,
    isGroupOwner,
    canModifyMember,
  }
}

export default useUserRole
